import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  MenuItem,
  MenuList,
  Spinner,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import download from 'downloadjs';
import { useHistory } from 'react-router';
import { Stack } from '@chakra-ui/layout';
import {
  Button,
  Heading,
  Input,
  Menu,
  Pagination,
  Select,
  StatusBadge,
  Table,
} from '~/components';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { useIsInternal, useQueryParams, useRoleCan } from '~/hooks';
import { useContracts } from '~/hooks/useContracts';
import { useDebounce } from '~/hooks/useDebounce';
import { getContractsXlsx } from '~/services';
import { PAGE_SIZE, ROUTER_PATHS } from '~/constants';
import { updateUrlParams } from '~/utils/updateUrlParams';
import { InsuranceStatus } from '~/pages/Portal/Contract/InsuranceStatus';
import { ZeroCarbonLogo } from '~/components/ZeroCarbonLogo';
import { ContractView } from '~/types';
import { queryClient } from '~/utils/react-query';
import { useFeatureFlags } from '~/contexts/FeatureFlagsContext/FeatureFlagsContext';

export const Contracts: React.VFC = () => {
  const queryParams = useQueryParams();
  const features = useFeatureFlags();
  const isClient = useRoleCan({ roles: ['cliente'] });
  const isAdmin = useRoleCan({ roles: ['administrador'] });
  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      situacao: queryParams.get('situacao') ?? '',
      situacaoFaturamento: queryParams.get('situacaoFaturamento') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
      order: queryParams.get('order') ?? '',
      orderBy: queryParams.get('orderBy') ?? '',
      seguro: queryParams.get('seguro') ?? '',
      certificado: queryParams.get('certificado') ?? '',
    };
  });

  const { groups, headOffices, branches } = useContext(FilterContext);
  const [listaContratos, setListaContratos] = useState<ContractView[]>([]);
  const { data, isLoading, isFetching, error, refetch } = useContracts(
    Number(parametros.pagina) ?? 1,
    parametros.busca,
    parametros.situacao,
    parametros.orderBy,
    parametros.order,
    parametros.situacaoFaturamento,
    parametros.seguro,
    parametros.certificado,
    { groups, headOffices, branches },
    '',
    '',
    '',
    {
      onSuccess: d => {
        setListaContratos(d.results);
      },
    },
    Number(parametros.paginacao),
  );
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const isInterno = useIsInternal();
  const roleCommercialManager = useRoleCan({ roles: ['gerente de contas'] });
  const canSeeInsurances = useRoleCan({
    roles: ['administrador', 'operacional', 'suporte'],
  });

  useDebounce(
    () => {
      setParametros({ ...parametros, busca: searchTmp, pagina: '1' });
      updateUrlParams(
        { ...parametros, busca: searchTmp, pagina: '1' },
        history,
      );
      refetch();
    },
    1000,
    [searchTmp],
  );

  const toast = useToast();

  useEffect(() => {
    queryClient.invalidateQueries('contracts');
  }, [data]);

  useEffect(() => {
    setParametros({ ...parametros, pagina: '1' });
    updateUrlParams({ ...parametros, pagina: '1' }, history);
    refetch();
  }, [groups, headOffices, branches]);

  useEffect(() => {
    const pagina = queryParams.get('pagina');
    setParametros({
      ...parametros,
      pagina: pagina ?? '1',
      order: queryParams.get('order') ?? sortDirection,
      orderBy: queryParams.get('orderBy') ?? sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        pagina: pagina ?? '1',
        order: queryParams.get('order') ?? sortDirection,
        orderBy: queryParams.get('orderBy') ?? sortField,
      },
      history,
    );
  }, [location.search]);

  const [sortField, setSortField] = useState(
    queryParams.get('orderBy') ?? 'dt_encerramentocontrato',
  );
  const [sortDirection, setSortDirection] = useState(
    queryParams.get('order') ?? 'DESC',
  );

  const [selecionarTodos, setSelecionarTodos] = useState(false);

  function handleContractOrder(field: string) {
    setSortField(field);
    setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
  }

  useEffect(() => {
    setParametros({
      ...parametros,
      order: sortDirection,
      orderBy: sortField,
      pagina: queryParams.get('pagina') ?? '1',
    });
    updateUrlParams(
      {
        ...parametros,
        order: sortDirection,
        orderBy: sortField,
        pagina: queryParams.get('pagina') ?? '1',
      },
      history,
    );
  }, [sortField, sortDirection]);

  const getDirection = (key: string) => {
    return sortField === key ? sortDirection : undefined;
  };

  function handleContractStatusChange(e: any) {
    setParametros({
      ...parametros,
      situacao: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        situacao: e.target.value,
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  }

  const handleFaturamentoChange = (e: any) => {
    setParametros({
      ...parametros,
      situacaoFaturamento: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        situacaoFaturamento: e.target.value,
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  };

  const handleSeguroChange = (e: any) => {
    setParametros({
      ...parametros,
      seguro: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        seguro: e.target.value,
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  };

  const handlePageSizeChange = (e: any) => {
    setParametros({
      ...parametros,
      paginacao: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        paginacao: e.target.value,
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
    queryClient.invalidateQueries('contracts');
  };

  const handleCertificadoChange = (e: any) => {
    setParametros({
      ...parametros,
      certificado: e.target.value,
      pagina: '1',
      order: sortDirection,
      orderBy: sortField,
    });
    updateUrlParams(
      {
        ...parametros,
        certificado: e.target.value,
        pagina: '1',
        order: sortDirection,
        orderBy: sortField,
      },
      history,
    );
  };

  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getContractsXlsx(
      Number(parametros.pagina) ?? 1,
      parametros.busca,
      parametros.situacao,
      parametros.orderBy,
      parametros.order,
      parametros.situacaoFaturamento,
      parametros.seguro,
      parametros.certificado,
      { groups, headOffices, branches },
    )
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  const selecionarTodosFn = () => {
    const selectTodos = !selecionarTodos;
    setSelecionarTodos(selectTodos);

    if (selectTodos) {
      const primeiroGrupo = listaContratos.find(c => c.pode_rescisao)?.empresas
        ?.grupoeconomico?.id_grupoeconomico;

      const selecionar = listaContratos.map(c => {
        const podeSelecionar =
          c.empresas?.grupoeconomico?.id_grupoeconomico === primeiroGrupo &&
          c.pode_rescisao;
        return {
          ...c,
          selecionado: podeSelecionar,
          desabilitado: !podeSelecionar,
        };
      });

      setListaContratos(selecionar);
    } else {
      setListaContratos(
        listaContratos.map(c => ({
          ...c,
          selecionado: false,
          desabilitado: false,
        })),
      );
    }
  };

  const selecionarContratoFn = (contratoSelecionado: ContractView) => {
    if (!contratoSelecionado.pode_rescisao) {
      return; // Não permite seleção se `pode_rescisao` for `false`
    }

    const contratosSelecionados = listaContratos.filter(
      contrato => contrato.selecionado,
    ).length;

    const atualizarStatusContratos = (desabilitar: boolean) =>
      listaContratos.map(contrato => ({
        ...contrato,
        desabilitado:
          contrato.empresas?.grupoeconomico?.id_grupoeconomico !==
            contratoSelecionado.empresas?.grupoeconomico?.id_grupoeconomico &&
          desabilitar,
      }));

    const alternarSelecaoContrato = (contratos: ContractView[]) =>
      contratos.map(contrato =>
        contrato.id_contrato === contratoSelecionado.id_contrato
          ? { ...contrato, selecionado: !contrato.selecionado }
          : contrato,
      );

    const verificarSelecionarTodos = (contratos: ContractView[]) => {
      const contratosDoGrupo = contratos.filter(
        contrato =>
          contrato.empresas?.grupoeconomico?.id_grupoeconomico ===
            contratoSelecionado.empresas?.grupoeconomico?.id_grupoeconomico &&
          contrato.pode_rescisao,
      );
      const todosSelecionados = contratosDoGrupo.every(
        contrato => contrato.selecionado,
      );
      setSelecionarTodos(todosSelecionados);
    };

    if (contratosSelecionados === 0) {
      const contratosAtualizados = atualizarStatusContratos(true);
      const contratosComSelecaoAtualizada =
        alternarSelecaoContrato(contratosAtualizados);
      setListaContratos(contratosComSelecaoAtualizada);
      verificarSelecionarTodos(contratosComSelecaoAtualizada);
      return;
    }

    if (contratosSelecionados === 1 && contratoSelecionado.selecionado) {
      setSelecionarTodos(false);
      const contratosAtualizados = atualizarStatusContratos(false);
      const contratosComSelecaoAtualizada =
        alternarSelecaoContrato(contratosAtualizados);
      setListaContratos(contratosComSelecaoAtualizada);
      return;
    }

    const contratosComSelecaoAtualizada =
      alternarSelecaoContrato(listaContratos);
    setListaContratos(contratosComSelecaoAtualizada);
    verificarSelecionarTodos(contratosComSelecaoAtualizada);
  };

  const existeContratosSelecionados = () => {
    return listaContratos.some(contrato => contrato.selecionado);
  };

  const irParaSolicitarRescisao = () => {
    const idsSelecionados = listaContratos
      .filter(contrato => contrato.selecionado)
      .map(contrato => contrato.nu_contrato);

    const qp = `contractIds=${idsSelecionados.join(',')}${
      isAdmin ? '&simulation=true' : ''
    }`;

    history.push(`${ROUTER_PATHS.PORTAL.REQUEST_CONTRACT_TERMINATION}?${qp}`);
  };

  return (
    <Box px="8" py="4">
      <Heading>Contratos</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={3} flexWrap="wrap">
          <Flex align="end" gridGap={4} mb={4}>
            <HStack align="end" gridGap={4} flexWrap="wrap">
              <Stack maxW="30%">
                <Input
                  name="search"
                  type="text"
                  icon={FiSearch}
                  value={searchTmp}
                  onChange={e => setSearchTmp(e.target.value)}
                  placeholder="Procure por contrato, pedido locação, CNPJ, Grupo Econômico ou Nº de série"
                  w={490}
                  fontSize="15px"
                />
              </Stack>
              <Stack maxW="30%">
                <Select
                  name="status"
                  label="Situação"
                  onChange={e => handleContractStatusChange(e)}
                  value={parametros.situacao}
                >
                  <option value="">Todos</option>
                  <option value="1">Ativos</option>
                  <option value="0">Inativos</option>
                </Select>
              </Stack>
              <Stack maxW="30%">
                <Select
                  name="faturamento"
                  label="Situação do Faturamento"
                  onChange={e => handleFaturamentoChange(e)}
                  value={parametros.situacaoFaturamento}
                  width="200px"
                >
                  <option value="">Todos</option>
                  <option value="0">Liberados</option>
                  <option value="1">Aguardando reajuste</option>
                </Select>
              </Stack>
              {canSeeInsurances && (
                <Stack maxW="30%">
                  <Select
                    name="seguro"
                    label="Contratos com seguro"
                    onChange={e => handleSeguroChange(e)}
                    value={parametros.seguro}
                    width="200px"
                  >
                    <option value="">Todos</option>
                    <option value="0">Sim</option>
                    <option value="1">Não</option>
                  </Select>
                </Stack>
              )}
              <Stack maxW="30%">
                <Select
                  name="certificado"
                  label="Certificado Zero Carbon"
                  onChange={e => handleCertificadoChange(e)}
                  value={parametros.certificado}
                  width="200px"
                >
                  <option value="">Todos</option>
                  <option value="0">Certificado emitido</option>
                  <option value="1">Certificado solicitado</option>
                  <option value="2">Certificado pendente</option>
                  <option value="3">Sem compensação</option>
                </Select>
              </Stack>
            </HStack>
            {isFetching && <Spinner mb={2} />}
          </Flex>
          <HStack gridGap={2}>
            <Button
              disabled={isLoadingDownload || isLoading}
              onClick={handleDownload}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
            >
              Exportar
              {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            {features.solicitarRescisaoContratosEnabled &&
              (isAdmin || isClient) && (
                <Button
                  disabled={!existeContratosSelecionados()}
                  onClick={() => irParaSolicitarRescisao()}
                  color="white"
                  background="gray.900"
                  _hover={{
                    bgColor: 'black',
                  }}
                  style={{ marginTop: '20px' }}
                >
                  {isAdmin
                    ? 'Simular rescisão de contrato'
                    : 'Solicitar rescisão de contrato'}
                </Button>
              )}
          </HStack>
        </Flex>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex justify="center">
            <Text>Falha ao obter dados dos contratos.</Text>
          </Flex>
        ) : (
          <>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    {features.solicitarRescisaoContratosEnabled &&
                      (isAdmin || isClient) && (
                        <Th textAlign="center" pe={1} minW="50px">
                          <Checkbox
                            colorScheme="b"
                            isChecked={selecionarTodos}
                            onChange={() => {
                              selecionarTodosFn();
                            }}
                            fontSize="sm"
                          />
                        </Th>
                      )}
                    <Th>N° Contrato</Th>
                    <Th>N° Pedido Locação</Th>
                    <Th>CNPJ</Th>
                    <Th>Empresa</Th>
                    <Th>Grupo</Th>
                    <Th>Qtd. Itens</Th>
                    <Th>
                      <Flex
                        gap={2}
                        cursor="pointer"
                        onClick={() => handleContractOrder('vr_totalcontrato')}
                      >
                        Valor Mensal Atual{' '}
                        {getDirection('vr_totalcontrato') === 'ASC' ? (
                          <ChevronDownIcon fontSize={14} ml="2" />
                        ) : (
                          <ChevronUpIcon fontSize={14} ml="2" />
                        )}
                      </Flex>
                    </Th>
                    <Th>
                      <Flex
                        gap={2}
                        cursor="pointer"
                        onClick={() =>
                          handleContractOrder('dt_encerramentocontrato')
                        }
                      >
                        Encerramento{' '}
                        {getDirection('dt_encerramentocontrato') === 'ASC' ? (
                          <ChevronDownIcon fontSize={14} ml="2" />
                        ) : (
                          <ChevronUpIcon fontSize={14} ml="2" />
                        )}
                      </Flex>
                    </Th>
                    <Th>Situação Pagamento</Th>
                    <Th>Situação Faturamento</Th>
                    {isInterno && !roleCommercialManager && (
                      <Th>Com seguro?</Th>
                    )}
                    <Th />
                    {features.solicitarRescisaoContratosEnabled &&
                      (isAdmin || isClient) && <Th textAlign="center">AÇÃO</Th>}
                    <Th display={{ md: 'none', sm: 'none' }} />
                  </Tr>
                </Thead>
                <Tbody>
                  {listaContratos &&
                    listaContratos.map(contract => (
                      <Tr key={`${contract.id_contrato}`}>
                        {features.solicitarRescisaoContratosEnabled &&
                          (isAdmin || isClient) && (
                            <Td textAlign="center" pe={1} w="50px">
                              <Checkbox
                                colorScheme="b"
                                isChecked={contract.selecionado}
                                isDisabled={
                                  contract.desabilitado ||
                                  !contract.pode_rescisao
                                }
                                onChange={() => {
                                  selecionarContratoFn(contract);
                                }}
                                fontSize="sm"
                              />
                            </Td>
                          )}
                        <Td>
                          <Link
                            to={`/portal/contracts/${contract.id_contrato}`}
                          >
                            <Text
                              _hover={{
                                color: 'gray.900',
                                textDecoration: 'underline',
                              }}
                            >
                              {contract.nu_contrato}
                            </Text>
                          </Link>
                        </Td>
                        <Td>{contract.nu_pedido}</Td>
                        <Td style={{ whiteSpace: 'nowrap' }}>
                          {contract.empresas?.cnpjFormatado || '-'}
                        </Td>
                        <Td>{contract.empresas?.nm_razaosocial || '-'}</Td>
                        <Td>
                          {contract.empresas?.grupoeconomico
                            ?.nm_grupoeconomico || '-'}
                        </Td>
                        <Td>{contract.quantity}</Td>
                        <Td>{contract.formattedTotalValue}</Td>
                        <Td>{contract.mes_encerramentocontrato}</Td>
                        <Td>
                          <StatusBadge variant={contract.colorScheme}>
                            {contract.status_pgto}
                          </StatusBadge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              contract.st_faturamento === 0 ? 'green' : 'red'
                            }
                            px={2}
                            py={1}
                            borderRadius={4}
                            w="150px"
                            textAlign="center"
                            fontSize="10px"
                          >
                            {contract.stFaturamentoDesc}
                          </Badge>
                        </Td>
                        {isInterno && !roleCommercialManager && (
                          <Td textAlign="center">
                            <InsuranceStatus
                              status={contract.apolice?.situacao}
                              dias={contract.apolice?.dias_vencimento}
                            />
                          </Td>
                        )}
                        <Td>
                          <Tooltip
                            hasArrow
                            label="Certificado emitido"
                            bg="black"
                            shouldWrapChildren
                          >
                            <ZeroCarbonLogo
                              emitido={contract.certificado_zero_carbono === 0}
                            />
                          </Tooltip>

                          <Tooltip
                            hasArrow
                            label="Certificado solicitado"
                            bg="black"
                            shouldWrapChildren
                          >
                            <ZeroCarbonLogo
                              notificacao_solicitado={
                                contract.certificado_zero_carbono === 1
                              }
                            />
                          </Tooltip>

                          <Tooltip
                            hasArrow
                            label="Certificado pendente de emissão"
                            bg="black"
                            shouldWrapChildren
                          >
                            <ZeroCarbonLogo
                              notificacao_pendente={
                                contract.certificado_zero_carbono === 2
                              }
                            />
                          </Tooltip>
                        </Td>
                        {features.solicitarRescisaoContratosEnabled &&
                          isClient && (
                            <Td>
                              <Menu
                                isDisabled={
                                  !contract.pode_rescisao ||
                                  contract.desabilitado
                                }
                              >
                                <MenuList>
                                  <Link
                                    to={`/portal/request-contract-termination?contractIds=${contract.nu_contrato}`}
                                  >
                                    <MenuItem>Solicitar rescisão</MenuItem>
                                  </Link>
                                </MenuList>
                              </Menu>
                            </Td>
                          )}
                        {features.solicitarRescisaoContratosEnabled && isAdmin && (
                          <Td>
                            <Menu
                              isDisabled={
                                !contract.pode_rescisao || contract.desabilitado
                              }
                            >
                              <MenuList>
                                <Link
                                  to={`/portal/request-contract-termination?simulation=true&contractIds=${contract.nu_contrato}`}
                                >
                                  <MenuItem>Simular rescisão</MenuItem>
                                </Link>
                              </MenuList>
                            </Menu>
                          </Td>
                        )}
                        <Td display={{ md: 'none', sm: 'none' }}>
                          <Menu>
                            <MenuList>
                              <Link
                                to={`/portal/contracts/${contract.id_contrato}`}
                              >
                                <MenuItem>Visualizar detalhes</MenuItem>
                              </Link>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableContainer>

            <Flex justify="flex-end">
              {data && (
                <Pagination
                  totalCountOfRegisters={data.count}
                  currentPage={Number(parametros.pagina) ?? 1}
                  onPageChange={_page => {
                    setParametros({ ...parametros, pagina: _page.toString() });
                    updateUrlParams(
                      { ...parametros, pagina: _page.toString() },
                      history,
                    );
                    queryClient.invalidateQueries('contracts');
                  }}
                  onPageSizeChange={handlePageSizeChange}
                  registersPerPage={Number(parametros.paginacao) ?? PAGE_SIZE}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};
